/**
 * OmniLeagues - Booking Calendar Panel (Scheduler-owned)
 * Hosts two sub-tabs: Event Types (with editor) + Requests Inbox.
 * Loaded inside the Dashboard "Booking Calendar" tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  CalendarClock,
  ExternalLink,
  Copy,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Loader2,
  Inbox,
  RefreshCw,
  Link2,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import {
  listEventTypes,
  createEventType,
  updateEventType,
  deleteEventType,
  rotateLink,
  listRequests,
  approveRequest,
  declineRequest,
  formatInTz,
  formatTimeOnly,
  dateKeyInTz,
  publicSlots,
  DAYS_OF_WEEK,
} from './bookingApi';

export default function BookingCalendarPanel({ orgId }) {
  const { isDark } = useTheme();
  const [subTab, setSubTab] = useState('preview');

  const shell = isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-6" data-testid="booking-calendar-panel">
      <Card className={shell}>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-amber-500" />
                Scheduler Calendar
              </CardTitle>
              <p className={`text-sm mt-1 ${muted}`}>
                Publish a public availability link. Opposing teams pick an open time slot and submit
                a game proposal — choosing their sport/level themselves. Nothing is written to
                WordPress until you approve the request.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={subTab} onValueChange={setSubTab}>
            <TabsList>
              <TabsTrigger value="preview" data-testid="booking-subtab-preview">
                Preview
              </TabsTrigger>
              <TabsTrigger value="types" data-testid="booking-subtab-types">
                Availability & Event Types
              </TabsTrigger>
              <TabsTrigger value="inbox" data-testid="booking-subtab-inbox">
                Requests Inbox
              </TabsTrigger>
            </TabsList>
            <TabsContent value="preview" className="mt-4">
              <AvailabilityPreviewTab orgId={orgId} />
            </TabsContent>
            <TabsContent value="types" className="mt-4">
              <EventTypesTab orgId={orgId} />
            </TabsContent>
            <TabsContent value="inbox" className="mt-4">
              <RequestsInboxTab orgId={orgId} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AVAILABILITY PREVIEW TAB — month grid preview (Calendly-style) of open slots
// ---------------------------------------------------------------------------
function AvailabilityPreviewTab({ orgId }) {
  const { isDark } = useTheme();
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthRef, setMonthRef] = useState(() => new Date());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const border = isDark ? 'border-slate-700' : 'border-slate-200';
  const dayOpen = isDark ? 'bg-amber-500/20 hover:bg-amber-500/40 text-amber-200' : 'bg-amber-100 hover:bg-amber-200 text-amber-900';
  const dayClosed = isDark ? 'text-slate-600' : 'text-slate-400';

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await listEventTypes(orgId);
        const data = res.data || [];
        setRows(data);
        const active = data.find((r) => r.active) || data[0];
        if (active) setSelectedId(active.id);
      } catch {
        toast.error('Failed to load event types');
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  const selected = rows.find((r) => r.id === selectedId) || null;
  const tz = selected?.timezone || 'America/New_York';

  useEffect(() => {
    if (!selected) {
      setSlots([]);
      return;
    }
    (async () => {
      setLoadingSlots(true);
      try {
        const y = monthRef.getFullYear();
        const m = monthRef.getMonth();
        const fromDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(y, m + 1, 0).getDate();
        const toDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        const res = await publicSlots(orgId, selected.slug, fromDate, toDate);
        setSlots(res.slots || []);
        setSelectedDate(null);
      } catch {
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, [selected, monthRef, orgId]);

  const slotsByDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      const k = dateKeyInTz(s.start_utc, tz);
      if (!map[k]) map[k] = [];
      map[k].push(s);
    });
    return map;
  }, [slots, tz]);

  const monthGrid = useMemo(() => {
    const y = monthRef.getFullYear();
    const m = monthRef.getMonth();
    const first = new Date(y, m, 1);
    const pad = first.getDay();
    const last = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < pad; i++) cells.push(null);
    for (let d = 1; d <= last; d++) cells.push(new Date(y, m, d));
    return cells;
  }, [monthRef]);

  const keyOf = (d) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

  const copyLink = (row) => {
    const url = `${window.location.origin}/book/${orgId}/${row.slug}?token=${row.public_token || ''}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading availability…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={`rounded-lg border-2 border-dashed ${border} p-10 text-center ${muted}`}>
        <Eye className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium mb-1">No availability published yet</p>
        <p className="text-sm">
          Go to <strong>Availability & Event Types</strong> and create your first event type (e.g.,
          "Home Game Slots") to open your public calendar.
        </p>
      </div>
    );
  }

  const monthLabel = monthRef.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const publicUrl = selected
    ? `${window.location.origin}/book/${orgId}/${selected.slug}?token=${selected.public_token || ''}`
    : '';

  return (
    <div className="space-y-4" data-testid="booking-preview-tab">
      {/* Event type switcher */}
      <div className="flex flex-wrap gap-2">
        {rows.map((r) => (
          <Button
            key={r.id}
            size="sm"
            variant={selectedId === r.id ? 'default' : 'outline'}
            className={selectedId === r.id ? 'bg-amber-600 hover:bg-amber-700' : ''}
            onClick={() => setSelectedId(r.id)}
            data-testid={`booking-preview-select-${r.slug}`}
          >
            <span
              className="inline-block w-2 h-2 rounded-full mr-2"
              style={{ backgroundColor: r.color || '#0ea5e9' }}
            />
            {r.name}
            {!r.active && <span className="ml-2 text-xs opacity-70">(inactive)</span>}
          </Button>
        ))}
      </div>

      {selected && (
        <div className={`grid md:grid-cols-2 gap-0 rounded-xl overflow-hidden border ${border}`}>
          {/* Left: event details */}
          <div className={`p-5 ${isDark ? 'bg-slate-900/40 border-r border-slate-800' : 'bg-slate-50 border-r border-slate-200'}`}>
            <div className={`text-xs uppercase tracking-wider ${muted} mb-1`}>Public Link</div>
            <h3 className="text-lg font-bold flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: selected.color }} />
              {selected.name}
            </h3>
            {selected.description && (
              <p className={`text-sm ${muted} mb-3`}>{selected.description}</p>
            )}
            <div className={`text-sm ${muted} space-y-1 mb-4`}>
              <div>{selected.duration_minutes} min · {selected.slot_increment_minutes} min increments</div>
              <div>Timezone: {tz}</div>
              {(selected.sport_options || []).length > 0 && (
                <div>
                  Sport options: <span className="text-amber-500">{selected.sport_options.join(', ')}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => copyLink(selected)} data-testid="booking-preview-copy">
                <Copy className="h-4 w-4 mr-1" /> Copy Link
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href={`/book/${orgId}/${selected.slug}?token=${selected.public_token || ''}`} target="_blank" rel="noreferrer" data-testid="booking-preview-open">
                  <ExternalLink className="h-4 w-4 mr-1" /> Open Public Page
                </a>
              </Button>
            </div>
            <div className={`mt-4 text-xs ${muted} break-all`}>{publicUrl}</div>
          </div>

          {/* Right: month preview */}
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMonthRef(new Date(monthRef.getFullYear(), monthRef.getMonth() - 1, 1))}
                data-testid="booking-preview-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="font-semibold">{monthLabel}</div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMonthRef(new Date(monthRef.getFullYear(), monthRef.getMonth() + 1, 1))}
                data-testid="booking-preview-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className={`grid grid-cols-7 text-center text-[11px] ${muted} mb-1`}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 mb-3">
              {monthGrid.map((d, i) => {
                if (!d) return <div key={i} />;
                const k = keyOf(d);
                const count = (slotsByDate[k] || []).length;
                const isSelected = selectedDate === k;
                const has = count > 0;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => has && setSelectedDate(k)}
                    disabled={!has}
                    className={`aspect-square rounded-md text-sm flex flex-col items-center justify-center transition ${
                      isSelected
                        ? 'bg-amber-600 text-white'
                        : has
                        ? dayOpen
                        : dayClosed
                    }`}
                    data-testid={has ? `booking-preview-day-${k}` : undefined}
                    title={has ? `${count} open slot${count === 1 ? '' : 's'}` : 'No open slots'}
                  >
                    <span>{d.getDate()}</span>
                    {has && <span className="text-[9px] opacity-80">{count}</span>}
                  </button>
                );
              })}
            </div>
            {loadingSlots && (
              <div className={`text-sm ${muted} flex items-center gap-2`}>
                <Loader2 className="h-4 w-4 animate-spin" /> Loading slots…
              </div>
            )}
            {!loadingSlots && slots.length === 0 && (
              <p className={`text-sm ${muted} text-center py-4`}>
                No open slots in this month. Check "Availability & Event Types" to widen the weekly
                hours or horizon.
              </p>
            )}
            {selectedDate && slotsByDate[selectedDate] && (
              <div className={`mt-2 rounded-lg border ${border} p-3`}>
                <div className="text-sm font-semibold mb-2">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric',
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {slotsByDate[selectedDate].map((s) => (
                    <span
                      key={s.start_utc}
                      className={`text-xs px-2 py-1 rounded-md border ${border}`}
                    >
                      {formatTimeOnly(s.start_utc, tz)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EVENT TYPES TAB
// ---------------------------------------------------------------------------
function EventTypesTab({ orgId }) {
  const { isDark } = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listEventTypes(orgId);
      setRows(res.data || []);
    } catch (e) {
      toast.error('Failed to load event types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleSave = async (payload) => {
    try {
      if (editing?.id) {
        await updateEventType(orgId, editing.id, payload);
        toast.success('Event type updated');
      } else {
        await createEventType(orgId, payload);
        toast.success('Event type created');
      }
      setEditorOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event type? Existing requests will keep their history.')) return;
    try {
      await deleteEventType(orgId, id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleRotate = async (id) => {
    if (!window.confirm('Rotate the public link? The old URL will stop working.')) return;
    try {
      await rotateLink(orgId, id);
      toast.success('Public link rotated');
      load();
    } catch {
      toast.error('Rotate failed');
    }
  };

  const copyLink = (row) => {
    const url = `${window.location.origin}/book/${orgId}/${row.slug}?token=${row.public_token || ''}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied');
  };

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const border = isDark ? 'border-slate-700' : 'border-slate-200';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className={`text-sm ${muted}`}>
          {loading ? 'Loading…' : `${rows.length} event type${rows.length === 1 ? '' : 's'}`}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="booking-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="booking-create-event-type-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Event Type
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          className={`rounded-lg border-2 border-dashed ${border} p-10 text-center ${muted}`}
          data-testid="booking-empty"
        >
          <CalendarClock className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium mb-1">No event types yet</p>
          <p className="text-sm">
            Create your first event type (for example, "Varsity Boys Basketball — Home") to generate
            a public booking link.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {rows.map((row) => (
            <EventTypeRow
              key={row.id}
              row={row}
              orgId={orgId}
              onEdit={() => {
                setEditing(row);
                setEditorOpen(true);
              }}
              onDelete={() => handleDelete(row.id)}
              onCopy={() => copyLink(row)}
              onRotate={() => handleRotate(row.id)}
            />
          ))}
        </div>
      )}

      {editorOpen && (
        <EventTypeEditor
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditing(null);
          }}
          initial={editing}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function EventTypeRow({ row, orgId, onEdit, onDelete, onCopy, onRotate }) {
  const { isDark } = useTheme();
  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-50 border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  const dayLabels = (row.weekly_hours || [])
    .map((w) => DAYS_OF_WEEK.find((d) => d.value === w.day)?.label)
    .filter(Boolean)
    .join(', ');

  const publicUrl = `/book/${orgId}/${row.slug}?token=${row.public_token || ''}`;

  return (
    <div
      className={`rounded-lg border p-4 ${card}`}
      data-testid={`booking-event-row-${row.slug}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
            style={{ backgroundColor: row.color || '#0ea5e9' }}
          >
            <CalendarClock className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-2 flex-wrap">
              {row.name}
              {!row.active && (
                <Badge variant="outline" className="text-xs">
                  Inactive
                </Badge>
              )}
            </div>
            <div className={`text-xs ${muted} mt-0.5`}>/{row.slug}</div>
            <div className={`text-sm ${muted} mt-1`}>
              {row.duration_minutes} min · {row.slot_increment_minutes} min increments ·{' '}
              {row.timezone || 'America/New_York'}
            </div>
            <div className={`text-xs ${muted} mt-1`}>Weekly: {dayLabels || '—'}</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onCopy}
            data-testid={`booking-copy-link-${row.slug}`}
          >
            <Copy className="h-4 w-4 mr-1" /> Copy Link
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`booking-open-public-${row.slug}`}
            >
              <ExternalLink className="h-4 w-4 mr-1" /> Preview
            </a>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onRotate}
            title="Rotate public link"
            data-testid={`booking-rotate-${row.slug}`}
          >
            <Link2 className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            data-testid={`booking-edit-${row.slug}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="text-red-600"
            data-testid={`booking-delete-${row.slug}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EDITOR DIALOG
// ---------------------------------------------------------------------------
function EventTypeEditor({ open, onClose, initial, onSave }) {
  const isEdit = !!initial?.id;
  const [state, setState] = useState(() => ({
    name: initial?.name || '',
    description: initial?.description || '',
    color: initial?.color || '#0ea5e9',
    active: initial?.active ?? true,
    duration_minutes: initial?.duration_minutes ?? 120,
    buffer_before_minutes: initial?.buffer_before_minutes ?? 0,
    buffer_after_minutes: initial?.buffer_after_minutes ?? 30,
    min_lead_minutes: initial?.min_lead_minutes ?? 4320,
    booking_horizon_days: initial?.booking_horizon_days ?? 60,
    slot_increment_minutes: initial?.slot_increment_minutes ?? 30,
    daily_max: initial?.daily_max ?? 2,
    weekly_max: initial?.weekly_max ?? 6,
    timezone: initial?.timezone || 'America/New_York',
    default_home_away: initial?.default_home_away || 'home',
    weekly_hours:
      initial?.weekly_hours && initial.weekly_hours.length
        ? initial.weekly_hours
        : [{ day: 5, start: '10:00', end: '16:00' }],
    date_overrides: initial?.date_overrides || [],
    sport_options: (initial?.sport_options || []).join('\n'),
    wp_sport_id: initial?.wp_sport_id || '',
    wp_schedule_id: initial?.wp_schedule_id || '',
    wp_venue_id: initial?.wp_venue_id || '',
    wp_home_team_id: initial?.wp_home_team_id || '',
  }));

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const updateRule = (idx, patch) => {
    setState((s) => ({
      ...s,
      weekly_hours: s.weekly_hours.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };
  const addRule = () =>
    setState((s) => ({
      ...s,
      weekly_hours: [...s.weekly_hours, { day: 0, start: '17:00', end: '21:00' }],
    }));
  const removeRule = (idx) =>
    setState((s) => ({
      ...s,
      weekly_hours: s.weekly_hours.filter((_, i) => i !== idx),
    }));

  const updateOverride = (idx, patch) => {
    setState((s) => ({
      ...s,
      date_overrides: s.date_overrides.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };
  const addOverride = () =>
    setState((s) => ({
      ...s,
      date_overrides: [
        ...s.date_overrides,
        { date: new Date().toISOString().slice(0, 10), type: 'block' },
      ],
    }));
  const removeOverride = (idx) =>
    setState((s) => ({
      ...s,
      date_overrides: s.date_overrides.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!state.name.trim()) return;
    const payload = { ...state };
    // Sport options: newline/comma separated -> trimmed string[]
    payload.sport_options = (state.sport_options || '')
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    // Coerce numeric-optional fields
    ['wp_sport_id', 'wp_schedule_id', 'wp_venue_id', 'wp_home_team_id'].forEach((k) => {
      if (payload[k] === '' || payload[k] == null) payload[k] = null;
      else payload[k] = Number(payload[k]);
    });
    await onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Event Type' : 'New Event Type'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basics */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Name *</Label>
              <Input
                value={state.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Varsity Boys Basketball — Home"
                required
                data-testid="booking-editor-name"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={state.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Home games at Main Gym. Buffer 30 min after."
              />
            </div>
            <div>
              <Label>Duration (minutes) *</Label>
              <Input
                type="number"
                min={15}
                value={state.duration_minutes}
                onChange={(e) => set('duration_minutes', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Slot increment (min)</Label>
              <Input
                type="number"
                min={5}
                value={state.slot_increment_minutes}
                onChange={(e) => set('slot_increment_minutes', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Buffer before (min)</Label>
              <Input
                type="number"
                min={0}
                value={state.buffer_before_minutes}
                onChange={(e) => set('buffer_before_minutes', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Buffer after (min)</Label>
              <Input
                type="number"
                min={0}
                value={state.buffer_after_minutes}
                onChange={(e) => set('buffer_after_minutes', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Min lead time (min)</Label>
              <Input
                type="number"
                min={0}
                value={state.min_lead_minutes}
                onChange={(e) => set('min_lead_minutes', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Booking horizon (days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={state.booking_horizon_days}
                onChange={(e) => set('booking_horizon_days', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Daily max games</Label>
              <Input
                type="number"
                min={0}
                value={state.daily_max}
                onChange={(e) => set('daily_max', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Weekly max games</Label>
              <Input
                type="number"
                min={0}
                value={state.weekly_max}
                onChange={(e) => set('weekly_max', Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Default side</Label>
              <select
                value={state.default_home_away}
                onChange={(e) => set('default_home_away', e.target.value)}
                className="w-full h-10 rounded-md border bg-transparent px-3"
              >
                <option value="home">Home</option>
                <option value="away">Away</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
            <div>
              <Label>Brand color</Label>
              <Input type="color" value={state.color} onChange={(e) => set('color', e.target.value)} />
            </div>
          </section>

          {/* Weekly hours */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base">Weekly Availability</Label>
              <Button type="button" size="sm" variant="outline" onClick={addRule}>
                <Plus className="h-3 w-3 mr-1" /> Add Window
              </Button>
            </div>
            <div className="space-y-2">
              {state.weekly_hours.map((w, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <select
                    value={w.day}
                    onChange={(e) => updateRule(i, { day: Number(e.target.value) })}
                    className="h-10 rounded-md border bg-transparent px-3"
                  >
                    {DAYS_OF_WEEK.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="time"
                    value={w.start}
                    onChange={(e) => updateRule(i, { start: e.target.value })}
                    className="w-32"
                  />
                  <span className="text-sm opacity-60">to</span>
                  <Input
                    type="time"
                    value={w.end}
                    onChange={(e) => updateRule(i, { end: e.target.value })}
                    className="w-32"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={() => removeRule(i)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          {/* Sport / Level options */}
          <section>
            <Label className="text-base">Sport / Level Options</Label>
            <p className="text-xs opacity-60 mb-2">
              Opposing teams must pick a sport/level when proposing a game. Leave empty to let them
              type freely. One option per line (or comma-separated).
            </p>
            <Textarea
              rows={3}
              value={state.sport_options}
              onChange={(e) => set('sport_options', e.target.value)}
              placeholder={'Varsity Boys Basketball\nJV Boys Basketball\n8th Grade Volleyball'}
              data-testid="booking-editor-sport-options"
            />
          </section>

          {/* Date overrides */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base">Date Overrides</Label>
              <Button type="button" size="sm" variant="outline" onClick={addOverride}>
                <Plus className="h-3 w-3 mr-1" /> Add Override
              </Button>
            </div>
            <div className="space-y-2">
              {state.date_overrides.length === 0 && (
                <p className="text-xs opacity-60">Add blackout days or one-off available windows.</p>
              )}
              {state.date_overrides.map((o, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="date"
                    value={o.date || ''}
                    onChange={(e) => updateOverride(i, { date: e.target.value })}
                    className="w-44"
                  />
                  <select
                    value={o.type}
                    onChange={(e) => updateOverride(i, { type: e.target.value })}
                    className="h-10 rounded-md border bg-transparent px-3"
                  >
                    <option value="block">Block full day</option>
                    <option value="add">Add window</option>
                  </select>
                  {o.type === 'add' && (
                    <>
                      <Input
                        type="time"
                        value={o.start || ''}
                        onChange={(e) => updateOverride(i, { start: e.target.value })}
                        className="w-32"
                      />
                      <span className="text-sm opacity-60">to</span>
                      <Input
                        type="time"
                        value={o.end || ''}
                        onChange={(e) => updateOverride(i, { end: e.target.value })}
                        className="w-32"
                      />
                    </>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => removeOverride(i)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </section>

          {/* WP linkage (optional) */}
          <section>
            <Label className="text-base">WordPress Game Linkage (optional)</Label>
            <p className="text-xs opacity-60 mb-2">
              Set these once and approved requests will auto-create WP games with the right sport,
              schedule, venue, and home team. Leave empty to approve OL-only.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <Label className="text-xs">Sport ID</Label>
                <Input
                  type="number"
                  value={state.wp_sport_id}
                  onChange={(e) => set('wp_sport_id', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Schedule ID</Label>
                <Input
                  type="number"
                  value={state.wp_schedule_id}
                  onChange={(e) => set('wp_schedule_id', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Venue ID</Label>
                <Input
                  type="number"
                  value={state.wp_venue_id}
                  onChange={(e) => set('wp_venue_id', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Home Team ID</Label>
                <Input
                  type="number"
                  value={state.wp_home_team_id}
                  onChange={(e) => set('wp_home_team_id', e.target.value)}
                />
              </div>
            </div>
          </section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="booking-editor-save"
            >
              {isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// REQUESTS INBOX
// ---------------------------------------------------------------------------
function RequestsInboxTab({ orgId }) {
  const { isDark } = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listRequests(orgId, filter === 'all' ? undefined : filter);
      setRows(res.data || []);
    } catch {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, filter]);

  const counts = useMemo(() => {
    const m = { pending: 0, approved: 0, declined: 0, cancelled: 0 };
    rows.forEach((r) => {
      if (m[r.status] != null) m[r.status]++;
    });
    return m;
  }, [rows]);

  const onApprove = async (r) => {
    if (!window.confirm(`Approve game request from ${r.requester.team_name}?`)) return;
    setBusyId(r.id);
    try {
      const res = await approveRequest(orgId, r.id);
      if (res?.wp_error) {
        toast.warning('Approved in OL. WP Bridge warning: ' + (res.wp_error.message || 'see logs'));
      } else {
        toast.success('Approved — game created on WordPress');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const onDecline = async () => {
    if (!declineFor) return;
    setBusyId(declineFor.id);
    try {
      await declineRequest(orgId, declineFor.id, declineReason);
      toast.success('Request declined');
      setDeclineFor(null);
      setDeclineReason('');
      load();
    } catch {
      toast.error('Decline failed');
    } finally {
      setBusyId(null);
    }
  };

  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-50 border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          {[
            ['pending', `Pending (${counts.pending})`],
            ['approved', `Approved (${counts.approved})`],
            ['declined', `Declined (${counts.declined})`],
            ['cancelled', `Cancelled (${counts.cancelled})`],
            ['all', 'All'],
          ].map(([v, label]) => (
            <Button
              key={v}
              size="sm"
              variant={filter === v ? 'default' : 'outline'}
              className={filter === v ? 'bg-amber-600 hover:bg-amber-700' : ''}
              onClick={() => setFilter(v)}
              data-testid={`booking-inbox-filter-${v}`}
            >
              {label}
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          className={`rounded-lg border-2 border-dashed p-10 text-center ${muted}`}
          data-testid="booking-inbox-empty"
        >
          <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Nothing here yet</p>
          <p className="text-sm">Share your booking link to start receiving requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className={`rounded-lg border p-4 ${card}`}
              data-testid={`booking-req-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={r.status} />
                    <div className="font-semibold">{r.event_type_name}</div>
                  </div>
                  <div className={`text-sm mt-1`}>
                    {formatInTz(r.slot_start_utc, r.timezone)} · {r.requester.team_name}
                    {r.requester.sport && (
                      <span className="ml-2 text-amber-500">[{r.requester.sport}]</span>
                    )}
                  </div>
                  <div className={`text-xs ${muted} mt-1`}>
                    {r.requester.name} · {r.requester.email}
                    {r.requester.phone ? ` · ${r.requester.phone}` : ''}
                    {r.requester.organization ? ` · ${r.requester.organization}` : ''}
                  </div>
                  {r.decline_reason && (
                    <div className="text-xs mt-1 text-red-500">
                      Declined: {r.decline_reason}
                    </div>
                  )}
                  {r.wp_game_id && (
                    <div className="text-xs mt-1 text-emerald-500">
                      WP Game ID: {r.wp_game_id}
                    </div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={busyId === r.id}
                      onClick={() => onApprove(r)}
                      data-testid={`booking-approve-${r.id}`}
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      disabled={busyId === r.id}
                      onClick={() => setDeclineFor(r)}
                      data-testid={`booking-decline-${r.id}`}
                    >
                      <X className="h-4 w-4 mr-1" /> Decline
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!declineFor} onOpenChange={(o) => !o && setDeclineFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              {declineFor?.requester?.team_name} · {formatInTz(declineFor?.slot_start_utc, declineFor?.timezone)}
            </p>
            <Label>Reason (sent to requester)</Label>
            <Textarea
              rows={3}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Field already booked for league play."
              data-testid="booking-decline-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineFor(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={onDecline}
              data-testid="booking-decline-confirm"
            >
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const color = {
    pending: 'bg-amber-500/20 text-amber-600 border-amber-500/40',
    approved: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/40',
    declined: 'bg-red-500/20 text-red-600 border-red-500/40',
    cancelled: 'bg-slate-500/20 text-slate-500 border-slate-500/40',
  }[status] || 'bg-slate-500/20 text-slate-500 border-slate-500/40';
  return (
    <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${color}`}>
      {status}
    </span>
  );
}
